import type { Company } from "../../api/addressBook.service.js";
import type {
  Award,
  AwardShortlistTag,
  AwardState,
  AwardStatus,
  AwardWriteBody,
  DictionaryRef,
} from "../../api/awards.types.js";
import type { CreatableSelectValue } from "../../components/Form/CreatableSelect/index.js";
import type { LawyerPickerValue } from "./components/LawyerPicker.js";

/** What the form holds. Rich values — a whole picker selection rather than an
 * id — because the pickers need a name to display and the write body needs the
 * id, and both come from the same selection. Product is the exception: a closed
 * list picked from a native select, so the id alone is enough. */
export type AwardFormValues = {
  productId: string;
  awardType: CreatableSelectValue;
  category: CreatableSelectValue;
  series: CreatableSelectValue;
  year: number;
  status: AwardStatus | "";
  /** `""` is "None". Only meaningful while `status` is `shortlisted`; the body
   * builder sends `null` for any other status whatever the field holds. */
  shortlistTag: AwardShortlistTag | "";
  company: Company | null;
  contact: LawyerPickerValue | null;
  jurisdictionCountryIds: number[];
  additionalInfo: string;
};

export const AWARD_STATUS_OPTIONS: Array<{ value: AwardStatus; label: string }> = [
  { value: "shortlisted", label: "Shortlisted" },
  { value: "winner", label: "Winner" },
  { value: "highly_commended", label: "Highly Commended" },
];

export const AWARD_SHORTLIST_TAG_OPTIONS: Array<{ value: AwardShortlistTag; label: string }> = [
  { value: "predicted_winner", label: "Predicted winner" },
  { value: "runner_up", label: "Runner-up" },
];

export const YEAR_MIN = 1900;
export const YEAR_MAX = 2100;

export function emptyAwardFormValues(): AwardFormValues {
  return {
    productId: "",
    awardType: null,
    category: null,
    series: null,
    year: new Date().getFullYear(),
    status: "",
    shortlistTag: "",
    company: null,
    contact: null,
    jurisdictionCountryIds: [],
    additionalInfo: "",
  };
}

function existingRef(id: string, name: string): CreatableSelectValue {
  return { kind: "existing", id, name };
}

/**
 * `CompanyPicker` takes a full `Company` but reads only `id` and `name`; an
 * award row carries exactly those two. The placeholders below never leave the
 * form — the write body takes `companyId` alone — and are here rather than a
 * cast so the compiler still checks the shape if `Company` grows.
 */
function companyFromAward(award: Award): Company | null {
  if (award.companyId === null) return null;
  return {
    id: award.companyId,
    name: award.firmName ?? award.firmNameRaw,
    companyId: 0,
    originalName: null,
    globalName: null,
    businessType: null,
    businessTypeId: null,
    firmRef: award.firmRef === null ? null : String(award.firmRef),
    slug: null,
    crmEntityNumber: null,
    website: null,
    industry: null,
    notes: null,
    createdAt: award.createdAt,
    updatedAt: award.updatedAt,
  };
}

export function awardToFormValues(award: Award): AwardFormValues {
  return {
    productId: award.productId,
    awardType: existingRef(award.awardTypeId, award.awardTypeName),
    category:
      award.categoryId !== null ? existingRef(award.categoryId, award.categoryName ?? "") : null,
    series: existingRef(award.seriesId, award.seriesName),
    year: award.year,
    status: award.status,
    shortlistTag: award.shortlistTag ?? "",
    company: companyFromAward(award),
    contact:
      award.contactId !== null
        ? { id: award.contactId, name: award.lawyerName ?? award.lawyerNameRaw }
        : null,
    jurisdictionCountryIds: award.jurisdictionCountryIds,
    additionalInfo: award.additionalInfo ?? "",
  };
}

function toDictionaryRef(value: NonNullable<CreatableSelectValue>): DictionaryRef {
  return value.kind === "existing" ? { id: value.id } : { name: value.name };
}

/**
 * Validation has already run by the time this is called, so the required
 * values are asserted rather than re-checked: a null here is a programming
 * error, not a user one.
 */
export function toAwardWriteBody(values: AwardFormValues, state: AwardState): AwardWriteBody {
  if (values.productId === "" || !values.awardType || !values.series) {
    throw new Error("Award dictionary fields are missing");
  }
  if (values.status === "" || !values.company || !values.contact) {
    throw new Error("Award status, firm or lawyer is missing");
  }

  const additionalInfo = values.additionalInfo.trim();

  return {
    product: { id: values.productId },
    awardType: toDictionaryRef(values.awardType),
    category: values.category ? toDictionaryRef(values.category) : null,
    series: toDictionaryRef(values.series),
    year: values.year,
    status: values.status,
    // The form already blanks the tag when the status leaves Shortlisted; this
    // is the guarantee on the wire, so a stale value can never reach the 400.
    shortlistTag:
      values.status === "shortlisted" && values.shortlistTag !== "" ? values.shortlistTag : null,
    state,
    companyId: values.company.id,
    contactId: values.contact.id,
    jurisdictionCountryIds: values.jurisdictionCountryIds,
    additionalInfo: additionalInfo === "" ? null : additionalInfo,
  };
}
